import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  Typography,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { kontram } from "../../../../theme/muiTheme";

// Export for testing purposes
export const rowColor = (item) => {
  if (item.category === "SampleCondition" && item.isActive === true) {
    return "color-danger-red-light";
  } else if (item.category === "Device" && item.isActive === true) {
    return "color-danger-red-light";
  } else if (item.category === "Warning" && item.isActive === true) {
    return "color-warning-orange";
  } else {
    return "color-normal-white";
  }
};

// Export for testing purposes
export const extraDiagnosticColor = (extraDiag, alarms) => {
  const alarm =
    alarms &&
    alarms.find(
      (alarm) =>
        alarm.hwId === extraDiag.hwId &&
        alarm.displayName === extraDiag.displayName
    );
  const diagColor = alarm ? rowColor(alarm) : "";
  return diagColor;
};

import CircleIcon from "@mui/icons-material/Circle";

// Export for testing purposes + added color as parameter
export const icon = (color) => {
  switch (color) {
    case "red":
      return <CircleIcon sx={{ color: kontram.red, fontSize: 24, mx: "1rem" }} />;
    case "orange":
      return <CircleIcon sx={{ color: kontram.warning, fontSize: 24, mx: "1rem" }} />;
    case "green":
      return <CircleIcon sx={{ color: kontram.primary, fontSize: 24, mx: "1rem" }} />;
    default:
      return;
  }
};


const DetailedDeviceModal = ({
  show,
  onHide,
  diagnostics,
  extraDiagnostics,
  measurements,
  title,
  color,
  systemId,
  hwId,
  isLora
}) => {
  const { t } = useTranslation();

  const hwIdAlarms = useSelector(
    ({ realtime }) =>
      realtime[systemId] &&
      realtime[systemId].alarms.entries &&
      realtime[systemId].alarms.entries[hwId]
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const rowColorMui = (item) => {
    if (item.category === "SampleCondition" && item.isActive === true) {
      return theme.palette.error.main;
    } else if (item.category === "Device" && item.isActive === true) {
      return theme.palette.error.main;
    } else if (item.category === "Warning" && item.isActive === true) {
      return theme.palette.warning.main;
    } else {
      return "inherit";
    }
  };

  const extraDiagnosticColorMui = (extraDiag, alarms) => {
    const alarm =
      alarms &&
      alarms.find(
        (alarm) =>
          alarm.hwId === extraDiag.hwId &&
          alarm.displayName === extraDiag.displayName
      );
    const diagColor = alarm ? rowColorMui(alarm) : "";
    return diagColor;
  };

  let ed =
    extraDiagnostics &&
    [].slice.call(Object.values(extraDiagnostics)).sort(function (a, b) {
      return a.displayName.localeCompare(b.displayName);
    });

  const diagnosticsContent = () => {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' }, mt: 1 }}>
        <Paper elevation={3} sx={{ flex: 1, p: 1 }}>
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }}
          >
            {t("Measurements")}
          </Typography>
          <Stack spacing={1}>
            {measurements &&
              measurements.map((item, idx) => (
                <Box
                  key={`${item.displayName}${idx}`}
                  sx={{ display: 'flex', gap: 2, borderBottom: '1px dotted', borderColor: theme.palette.divider }}
                >
                  <Typography sx={{ minWidth: { xs: '190px', sm: '230px', md: '260px', lg: '280px' } }} color={rowColorMui(item)}>
                    {item.displayName}
                  </Typography>
                  <Typography color={rowColorMui(item)}>
                    {`${item.value} ${item.unit || ""}`}
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Paper>
        <Paper elevation={3} sx={{ flex: 1, p: 1 }}>
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }}
          >
            {t("Diagnostics")}
          </Typography>
          <Stack spacing={1}>
            {diagnostics &&
              diagnostics.map((item, idx) => (
                <Box
                  key={`${item.displayName}${idx}`}
                  sx={{ display: 'flex', gap: 2, borderBottom: '1px dotted', borderColor: theme.palette.divider }}
                >
                  <Typography sx={{ minWidth: { xs: '190px', sm: '230px', md: '260px', lg: '280px' } }} color={rowColorMui(item)}>
                    {item.displayName}
                  </Typography>
                  <Typography color={rowColorMui(item)}>
                    {`${item.value} ${item.unit || ""}`}
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Paper>
        <Paper elevation={3} sx={{ flex: 1, p: 1 }}>
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', borderBottom: `1px solid ${theme.palette.divider}`, mb: 2 }}
          >
            {t("Details")}
          </Typography>
          <Stack spacing={1}>
            {ed &&
              Object.keys(ed) &&
              Object.keys(ed).length > 0 &&
              Object.keys(ed).map((diag, idx) => (
                <Box
                  key={`${ed[diag].displayName}${idx}`}
                  sx={{ display: 'flex', gap: 2, borderBottom: '1px dotted', borderColor: theme.palette.divider }}
                >
                  <Typography sx={{ minWidth: { xs: '190px', sm: '230px', md: '260px', lg: '280px' } }} color={extraDiagnosticColorMui(ed[diag], hwIdAlarms)}>
                    {ed[diag].displayName}
                  </Typography>
                  <Typography color={extraDiagnosticColorMui(ed[diag], hwIdAlarms)}>
                    {`${ed[diag].valueString ? ed[diag].valueString : ed[diag].value} ${ed[diag].unit || ""}`}
                  </Typography>
                </Box>
              ))}
          </Stack>
        </Paper>
      </Box>
    );
  };


  return (
    <Dialog
      open={show}
      onClose={onHide}
      fullScreen={isSmallScreen}
      sx={{
        '& .MuiDialog-paper': {
          width: { xs: '100%', sm: '100%', md: '90%', lg: '80%' },
          maxWidth: { xs: '100%', sm: '100%', md: '90%', lg: '80%' },
          height: { xs: '100%', sm: '100%', md: '90%', lg: '80%' },
          maxHeight: { xs: '100%', sm: '100%', md: '90%', lg: '80%' }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircleIcon sx={{ color: color }} />
          <Typography variant="h5">{title || ""}</Typography>
          {isLora && (
            <Typography variant="body2" >{hwId}</Typography>
          )}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onHide}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            overflow: 'auto',
          }}
        >
          {diagnosticsContent()}
          {/* {detailsContent()} */}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          size="large"
          onClick={onHide}
          variant="contained"
          color="success"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DetailedDeviceModal;
